/**
 * CreateBookingLauncher — app-shell-level provider for the GLOBAL "New
 * booking" entry point (BON-142), mirroring CreateWorkshopLauncher. Owns the
 * open-state for `CreateBookingModal`, exposes `openCreateBooking()` to the
 * command palette (G1) and to the on-page CTA/FAB, and registers the global
 * "c then b" keyboard sequence (G2) with the same typing/palette guards.
 *
 * Gating: either write:bookings OR write:comp-bookings unlocks the launcher —
 * the modal itself already gates the *comp* radio on write:comp-bookings, so
 * a comp-only manager can still open the modal to create a paid booking (and
 * vice versa). The palette command + shortcut are no-ops without either.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useCreateShortcut } from '../../../hooks/use-create-shortcut';
import { usePermissions } from '../../../hooks/usePermissions';
import { ErrorDialog } from '../../../shared/ErrorDialog';
import { Permission } from '../../../types/permissions';

import CreateBookingModal from './CreateBookingModal';
import { useHandleBookingCreated } from './useHandleBookingCreated';

/**
 * The global shortcut is a Linear/Gmail-style key SEQUENCE: press "c" then
 * "b" (create → booking), matching the "c then w" workshop shortcut. See `useCreateShortcut`.
 */
export const NEW_BOOKING_SHORTCUT_KEYS = ['C', 'B'] as const;
export const NEW_BOOKING_SHORTCUT_LABEL = NEW_BOOKING_SHORTCUT_KEYS.join(' then ');

interface LauncherValue {
  /** Open the global create-booking modal. No-op without permission. */
  openCreateBooking: () => void;
  /** Whether the viewer may create bookings — gates the palette command. */
  canCreateBooking: boolean;
}

const CreateBookingLauncherContext = createContext<LauncherValue>({
  openCreateBooking: () => {},
  canCreateBooking: false,
});

export function CreateBookingLauncherProvider({ children }: { children: ReactNode }) {
  const { hasAnyPermission } = usePermissions();
  const canCreateBooking = hasAnyPermission(
    Permission.WRITE_BOOKINGS,
    Permission.WRITE_COMP_BOOKINGS,
  );
  const [open, setOpen] = useState(false);
  // A reservation whose email failed to send — lives here, not in the modal,
  // because the modal is already gone by the time this shows.
  const [emailFailedFor, setEmailFailedFor] = useState<string | null>(null);

  const openCreateBooking = useCallback(() => {
    if (canCreateBooking) setOpen(true);
  }, [canCreateBooking]);

  const handleCreated = useHandleBookingCreated(() => setOpen(false), setEmailFailedFor);

  // G2 — global "c then b" sequence (create → booking).
  useCreateShortcut('b', canCreateBooking, () => setOpen(true));

  const value = useMemo(
    () => ({ openCreateBooking, canCreateBooking }),
    [openCreateBooking, canCreateBooking],
  );

  return (
    <CreateBookingLauncherContext.Provider value={value}>
      {children}
      {open && <CreateBookingModal onClose={() => setOpen(false)} onCreated={handleCreated} />}
      {emailFailedFor && (
        <ErrorDialog
          title="The booking link didn't send"
          message={`Seats are held, but the email to ${emailFailedFor} didn't go out. Use Resend link on the new reservation.`}
          tone="error"
          onClose={() => setEmailFailedFor(null)}
        />
      )}
    </CreateBookingLauncherContext.Provider>
  );
}

export function useCreateBookingLauncher(): LauncherValue {
  return useContext(CreateBookingLauncherContext);
}
